import { StandardEvents } from '@shopify/events';

const CEP_STORAGE_KEY = 'carraro:cep';
const QUOTE_CACHE_PREFIX = 'carraro:cart-quote:';
// Short on purpose: the same cart + CEP is re-quoted on every drawer open and
// page view, but carrier prices can change, so a quote isn't reused for long.
const QUOTE_CACHE_TTL_MS = 10 * 60 * 1000;
// Intelipost often answers after 2 s; ~10 s of polling lets a slow quote land
// instead of showing "nenhuma opção de frete". Kept in sync with shipping-estimator.liquid.
const ASYNC_RATES_ATTEMPTS = 20;

const normalizeCep = (value) => value.replace(/\D/g, '').slice(0, 8);
const formatCep = (value) => {
  const cep = normalizeCep(value);
  return cep.length > 5 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep;
};

/**
 * Sets a <text-component>'s displayed text. The `value` attribute is what
 * TextComponent's shimmer overlay renders (see the `[shimmer]::after`
 * CSS in base.css), so it has to stay in sync with the real text node.
 * @param {Element|null|undefined} el
 * @param {string} text
 */
function setText(el, text) {
  if (!(el instanceof HTMLElement)) return;
  el.textContent = text;
  el.setAttribute('value', text);
}

/**
 * Toggles the theme's native text-component shimmer loading state. The
 * shimmer overlay is painted from the `value` attribute (`content:
 * attr(value)` in base.css), so it needs real text to render — an empty
 * <text-component> has nothing to shimmer and would look like nothing
 * happened. "Calculando..." doubles as that text, shown with the shimmer
 * sweep instead of as a static string.
 * @param {Element|null|undefined} el
 * @param {boolean} isLoading
 */
function setShimmer(el, isLoading) {
  if (!(el instanceof HTMLElement)) return;

  if (isLoading) {
    el.textContent = 'Calculando...';
    el.setAttribute('value', 'Calculando...');
    el.setAttribute('shimmer', '');
  } else {
    el.removeAttribute('shimmer');
  }
}

const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const formatCents = (cents) => brlFormatter.format(cents / 100);

function formatShippingPrice(rate) {
  const value = parseFloat(rate.price);
  if (isNaN(value)) return rate.price;
  if (value === 0) return 'Grátis';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: rate.currency || 'BRL' });
}

/**
 * Estimates a rate's delivery time in days from now, for comparing rates
 * against each other. Returns null when the rate carries neither field (e.g.
 * some carrier-calculated rates only report a price).
 * @param {{delivery_days?: number[], delivery_range?: string[]}} rate
 */
function estimateDeliveryDays(rate) {
  if (rate.delivery_date) {
    const date = new Date(`${rate.delivery_date}T12:00:00`);
    if (!isNaN(date.getTime())) return Math.max(0, Math.round((date.getTime() - Date.now()) / 86400000));
  }
  if (Array.isArray(rate.delivery_days) && rate.delivery_days.length) {
    return rate.delivery_days[rate.delivery_days.length - 1];
  }
  if (Array.isArray(rate.delivery_range) && rate.delivery_range.length === 2) {
    const max = new Date(rate.delivery_range[1]);
    if (!isNaN(max.getTime())) return Math.max(0, Math.round((max.getTime() - Date.now()) / 86400000));
  }
  return null;
}

/**
 * Hides the real carrier/service name (e.g. "Jadlog .Package", "Correios
 * SEDEX") from the shopper without touching `rate.name` itself anywhere else
 * — this only swaps the label shown in the cart drawer/cart page, so the
 * actual quote name Shopify picked still lands on the order untouched once
 * checkout completes, for internal identification. Ranks by estimated
 * delivery time when every rate in the set reports one (fastest ->
 * "expressa"); falls back to ranking by price (priciest -> "expressa") when
 * that data isn't available, since the faster service is consistently the
 * pricier one. Kept in sync with the same masking in shipping-estimator.liquid.
 * @param {object} rate
 * @param {object[]} allRates
 */
function maskRateLabel(rate, allRates) {
  if (allRates.length < 2) return 'Entrega padrão';

  const days = allRates.map(estimateDeliveryDays);
  const haveDays = days.every((d) => d !== null);

  if (haveDays) {
    const fastestDays = Math.min(...days);
    return estimateDeliveryDays(rate) === fastestDays ? 'Entrega expressa' : 'Entrega padrão';
  }

  const prices = allRates.map((r) => parseFloat(r.price));
  const priciest = Math.max(...prices);
  return parseFloat(rate.price) === priciest ? 'Entrega expressa' : 'Entrega padrão';
}

/**
 * A CEP's city/state never changes, so each one is looked up on ViaCEP only
 * once per session — same key as shipping-estimator.liquid, so either
 * widget's lookup serves the other.
 * @param {string} zip
 */
async function lookupAddress(zip) {
  const cacheKey = 'carraro:viacep:' + zip;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch (_) {
    // sessionStorage can be unavailable in restricted browser contexts.
  }

  try {
    const res = await fetch('https://viacep.com.br/ws/' + zip + '/json/');
    const data = await res.json();
    if (data.erro) return null;
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (_) {
      // Caching is best-effort.
    }
    return data;
  } catch (e) {
    return null;
  }
}

/**
 * Identifies what a quote depends on: the CEP and exactly which items, in
 * which quantities, are in the cart.
 * @param {string} cep
 * @param {{items: {variant_id: number, quantity: number}[]}} cart
 */
function quoteCacheKey(cep, cart) {
  const items = cart.items.map((item) => `${item.variant_id}x${item.quantity}`).sort().join(',');
  return `${QUOTE_CACHE_PREFIX}${cep}:${items}`;
}

function getCachedQuote(key) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!cached || Date.now() - cached.timestamp > QUOTE_CACHE_TTL_MS) return null;
    return cached;
  } catch (_) {
    return null;
  }
}

function setCachedQuote(key, rates, address) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ rates, address, timestamp: Date.now() }));
  } catch (_) {
    // Caching is best-effort.
  }
}

async function fetchSyncRates(params) {
  const res = await fetch('/cart/shipping_rates.json?' + params.toString(), {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('Falha ao calcular o frete.');
  const data = await res.json();
  return data.shipping_rates || [];
}

// Carrier-calculated rates that need this fallback are inherently uncapped
// on the backend's own compute time — this only controls how long the
// shopper waits for OUR polling before giving up, not how long the carrier
// actually takes: up to ASYNC_RATES_ATTEMPTS polls, 300ms then 500ms apart.
async function fetchAsyncRates(params) {
  await fetch('/cart/prepare_shipping_rates.json?' + params.toString(), {
    method: 'POST',
    headers: { Accept: 'application/json' },
  });

  for (let attempt = 0; attempt < ASYNC_RATES_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 300 : 500));

    const res = await fetch('/cart/async_shipping_rates.json?' + params.toString(), {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) continue;

    const data = await res.json();
    if (data.shipping_rates && data.shipping_rates.length) return data.shipping_rates;
  }

  return [];
}

/**
 * Calculates real shipping rates for the current cart against a CEP: the
 * Ajax Cart API's synchronous endpoint, then the prepare/async flow some
 * carrier-calculated rates (e.g. Intelipost) only resolve through. Prices the
 * shopper's real cart as is, which measured faster (~1.3 s) than rebuilding it
 * on a Storefront API cart (~2.2 s). Doesn't wait on ViaCEP: the state comes
 * straight from the CEP.
 * @param {string} cep - 8-digit CEP, digits only.
 */
async function calculateShippingRates(cep) {
  const quoteApi = window.CarraroShippingQuote;
  const params = new URLSearchParams({
    'shipping_address[zip]': cep,
    'shipping_address[country]': 'BR',
  });
  const uf = quoteApi ? quoteApi.ufFromCep(cep) : '';
  if (uf) params.set('shipping_address[province]', uf);

  let rates = await fetchSyncRates(params);
  if (!rates.length) rates = await fetchAsyncRates(params);
  return rates;
}

/**
 * Persists the CEP (and resolved city/state, when available) onto the
 * cart's own attributes via the Ajax Cart API, so the information survives
 * page reloads / new tabs and is visible on the order once it's placed —
 * sessionStorage alone only re-hydrates the widgets on the same browser tab.
 * @param {string} cep
 * @param {{localidade?: string, uf?: string}|null} address
 */
async function persistCepToCart(cep, address) {
  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          CEP: formatCep(cep),
          Cidade: (address && address.localidade) || '',
          Estado: (address && address.uf) || '',
        },
      }),
    });
  } catch (_) {
    // Non-fatal: the shipping estimate still works off sessionStorage even if this fails.
  }
}

class CartShippingCep extends HTMLElement {
  /** @type {number} */
  #requestId = 0;

  /** true enquanto o cliente reabriu o campo pelo "Alterar" para trocar o CEP. */
  #editando = false;

  /** @type {MutationObserver | null} */
  #observador = null;

  #reaplicando = false;

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.form = this.querySelector('[data-shipping-cep-form]');
    this.input = this.querySelector('[data-shipping-cep-input]');
    this.savedCep = this.querySelector('[data-shipping-saved-cep]');
    this.cepEdit = this.querySelector('[data-shipping-cep-edit]');
    this.shippingValue = this.querySelector('[data-shipping-value]');
    this.message = this.querySelector('[data-shipping-cep-message]');
    this.quoteBubble = this.querySelector('[data-shipping-quote-bubble]');
    this.cartDrawer = this.closest('#cart-drawer');
    this.shippingTotal = this.closest('.cart-summary-page, .cart-summary-carraro')?.querySelector(
      '[data-shipping-total]'
    );

    this.input?.addEventListener('input', this.handleInput);
    this.form?.addEventListener('submit', this.handleSubmit);
    this.cepEdit?.addEventListener('click', this.handleEditCep);
    document.addEventListener('carraro:cep-change', this.handleExternalCepChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartLinesUpdate);
    this.cartDrawer?.addEventListener('theme-drawer:open', this.handleDrawerOpen);
    window.addEventListener('pageshow', this.handlePageShow);

    // Ao adicionar um item, a gaveta é redesenhada e o bloco volta ao estado do
    // servidor (campo vazio à mostra) sem novo connectedCallback: reaplica o CEP salvo.
    this.#observador = new MutationObserver(this.#aoRedesenhar);
    this.#observador.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden'] });

    this.restoreCep();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.handleInput);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.cepEdit?.removeEventListener('click', this.handleEditCep);
    document.removeEventListener('carraro:cep-change', this.handleExternalCepChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleCartLinesUpdate);
    this.cartDrawer?.removeEventListener('theme-drawer:open', this.handleDrawerOpen);
    window.removeEventListener('pageshow', this.handlePageShow);
    this.#observador?.disconnect();
    this.#observador = null;
    delete this.dataset.initialized;
  }

  /**
   * Once a CEP is saved, showShipping() hides the form and leaves only the
   * saved CEP + estimate on screen — with no way back to the input to try a
   * different one. This re-opens the form, pre-filled with the current CEP,
   * so the shopper can correct or replace it.
   */
  #aoRedesenhar = () => {
    if (this.#reaplicando || this.#editando) return;
    this.#reaplicando = true;
    requestAnimationFrame(() => {
      this.#reaplicando = false;
      if (this.#editando) return;
      const form = this.querySelector('[data-shipping-cep-form]');
      if (!(form instanceof HTMLElement) || form.hidden) return;
      let cep = '';
      try {
        cep = normalizeCep(sessionStorage.getItem(CEP_STORAGE_KEY) || '');
      } catch (_) {
        // sessionStorage can be unavailable in restricted browser contexts.
      }
      if (cep.length !== 8) return;
      // O redesenho pode trocar os nós: relê as referências antes de mostrar o frete.
      this.form = form;
      this.input = this.querySelector('[data-shipping-cep-input]');
      this.savedCep = this.querySelector('[data-shipping-saved-cep]');
      this.cepEdit = this.querySelector('[data-shipping-cep-edit]');
      this.shippingValue = this.querySelector('[data-shipping-value]');
      this.message = this.querySelector('[data-shipping-cep-message]');
      this.quoteBubble = this.querySelector('[data-shipping-quote-bubble]');
      this.shippingTotal = this.closest('.cart-summary-page, .cart-summary-carraro')?.querySelector('[data-shipping-total]');
      this.restoreCep();
    });
  };

  handleEditCep = () => {
    this.#editando = true;
    if (this.savedCep instanceof HTMLElement) this.savedCep.hidden = true;
    if (this.cepEdit instanceof HTMLElement) this.cepEdit.hidden = true;

    if (this.form instanceof HTMLElement) this.form.hidden = false;
    if (this.input instanceof HTMLInputElement) {
      try {
        this.input.value = formatCep(sessionStorage.getItem(CEP_STORAGE_KEY) || '');
      } catch (_) {
        // sessionStorage can be unavailable in restricted browser contexts.
      }
      this.input.focus();
      this.input.select();
    }
  };

  handleDrawerOpen = () => {
    // Reabrir a gaveta encerra uma edição de CEP que ficou pela metade.
    this.#editando = false;
    // The drawer remains mounted between openings, so always absorb the most
    // recent CEP instead of relying on the value read at page load.
    this.restoreCep();
  };

  handlePageShow = () => {
    this.restoreCep();
  };

  restoreCep() {
    try {
      const cep = normalizeCep(sessionStorage.getItem(CEP_STORAGE_KEY) || '');
      if (cep.length === 8) {
        this.showShipping(cep);
        this.updateShippingRates(cep);
      }
    } catch (_) {
      // sessionStorage can be unavailable in restricted browser contexts.
    }
  }

  handleInput = () => {
    if (!this.input) return;
    const cep = normalizeCep(this.input.value);
    this.input.value = formatCep(cep);
    if (cep.length === 8) this.saveAndShowShipping(cep);
  };

  handleSubmit = (event) => {
    event.preventDefault();
    if (!this.input) return;

    const cep = normalizeCep(this.input.value);
    if (cep.length === 8) {
      this.saveAndShowShipping(cep);
    } else if (this.message) {
      this.message.textContent = 'Digite um CEP válido com 8 números.';
      this.message.hidden = false;
    }
  };

  handleExternalCepChange = (event) => {
    const cep = normalizeCep(event.detail?.cep || '');
    if (cep.length === 8) {
      this.#editando = false;
      this.showShipping(cep);
      this.updateShippingRates(cep);
    }
  };

  /**
   * Line quantity changes (and adds/removals) affect carrier-calculated
   * rates just as much as the CEP does, but only the CEP used to trigger a
   * recalculation — bumping a line's quantity left the last quote on
   * screen stale. Waits for the line update's own promise so this reads
   * the cart *after* the change actually lands server-side, not before.
   * @param {CustomEvent<{promise?: Promise<any>}>} event
   */
  handleCartLinesUpdate = (event) => {
    const recalculate = () => {
      try {
        const cep = normalizeCep(sessionStorage.getItem(CEP_STORAGE_KEY) || '');
        if (cep.length === 8) this.updateShippingRates(cep);
      } catch (_) {
        // sessionStorage can be unavailable in restricted browser contexts.
      }
    };

    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(recalculate, recalculate);
    } else {
      recalculate();
    }
  };

  saveAndShowShipping(cep) {
    this.#editando = false;
    try {
      sessionStorage.setItem(CEP_STORAGE_KEY, cep);
    } catch (_) {
      // Continue showing the estimate even if storage is unavailable.
    }

    document.querySelectorAll('[data-cep-input]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = formatCep(cep);
    });
    document.dispatchEvent(new CustomEvent('carraro:cep-change', { detail: { cep } }));
    this.showShipping(cep);
    this.updateShippingRates(cep);
  }

  showShipping(cep, address) {
    if (this.form instanceof HTMLElement) this.form.hidden = true;
    if (this.savedCep instanceof HTMLElement) {
      const location = address && address.localidade && address.uf
        ? ` – ${address.localidade}/${address.uf}`
        : '';
      this.savedCep.textContent = `CEP ${formatCep(cep)}${location}`;
      this.savedCep.hidden = false;
    }
    if (this.cepEdit instanceof HTMLElement) this.cepEdit.hidden = false;
    if (this.shippingValue instanceof HTMLElement) this.shippingValue.hidden = false;
  }

  /**
   * Soma o frete cotado ao total do resumo (e à linha do Pix), para a conta fechar:
   * subtotal - descontos + frete = total. Sem cotação, o total fica só com os produtos.
   * Os valores dos produtos vêm do servidor (data-products-cents / data-pix-cents), que o
   * tema atualiza a cada mudança no carrinho; esta função roda de novo depois de cada cotação.
   * @param {number|null} shippingCents
   */
  applyTotals(shippingCents) {
    const root = this.closest('.cart-summary-page, .cart-summary-carraro');
    if (!root) return;
    const hasShipping = Number.isFinite(shippingCents);
    const shipping = hasShipping ? shippingCents : 0;

    const total = root.querySelector('[data-products-cents]');
    if (total instanceof HTMLElement) {
      setText(total, formatCents(Number(total.dataset.productsCents || 0) + shipping));
    }

    const pix = root.querySelector('[data-pix-total]');
    if (pix instanceof HTMLElement) {
      pix.textContent = formatCents(Number(pix.dataset.pixCents || 0) + shipping);
    }

    root.querySelectorAll('[data-total-note]').forEach((note) => {
      if (!hasShipping) note.textContent = 'Frete calculado ao informar o CEP';
      else note.textContent = shipping === 0 ? 'Frete grátis incluso' : 'Frete incluso';
    });
  }

  /**
   * Fetches real shipping rates for this CEP and updates the displayed
   * value (and the order summary's "Entrega / Frete" line, if present).
   * Both targets are <text-component> elements, so instead of a literal
   * "Calculando..." string this drives the theme's native shimmer effect
   * (see TextComponent.shimmer() in utilities.js) while the request is in
   * flight — same loading language the cart already uses for its total.
   * @param {string} cep
   */
  updateShippingRates = async (cep) => {
    if (!(this.shippingValue instanceof HTMLElement)) return;

    const requestId = ++this.#requestId;
    setShimmer(this.shippingValue, true);
    setShimmer(this.shippingTotal, true);
    if (this.message instanceof HTMLElement) {
      this.message.textContent = '';
      this.message.hidden = true;
    }

    try {
      /** @type {string|null} */
      let cacheKey = null;
      try {
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        cacheKey = quoteCacheKey(cep, cart);
      } catch (_) {
        // Without the cart there's nothing to key the cache on; just quote.
      }
      if (requestId !== this.#requestId) return;

      const cached = cacheKey && getCachedQuote(cacheKey);
      // Carrier quotes can take several seconds; the balloon tells the shopper it isn't stuck.
      if (!cached && this.quoteBubble instanceof HTMLElement) this.quoteBubble.hidden = false;

      const rates = cached ? cached.rates : await calculateShippingRates(cep);
      // A newer CEP came in while this request was in flight — drop this result.
      if (requestId !== this.#requestId) return;
      if (!cached && cacheKey && rates.length) setCachedQuote(cacheKey, rates, null);

      // The city name is only for display, so ViaCEP runs after the price is
      // already on screen instead of in front of it (cached per CEP anyway).
      this.showShipping(cep, cached ? cached.address : null);
      lookupAddress(cep).then((address) => {
        persistCepToCart(cep, address || { uf: window.CarraroShippingQuote?.ufFromCep(cep) || '' });
        if (!address || requestId !== this.#requestId) return;
        this.showShipping(cep, address);
        if (cacheKey && rates.length) setCachedQuote(cacheKey, rates, address);
      });

      setShimmer(this.shippingValue, false);
      setShimmer(this.shippingTotal, false);
      if (this.quoteBubble instanceof HTMLElement) this.quoteBubble.hidden = true;

      if (!rates.length) {
        setText(this.shippingValue, '—');
        setText(this.shippingTotal, '—');
        this.applyTotals(null);
        if (this.message instanceof HTMLElement) {
          this.message.textContent = 'Nenhuma opção de frete disponível para esse CEP.';
          this.message.hidden = false;
        }
        return;
      }

      const cheapest = rates.reduce(
        (min, rate) => (parseFloat(rate.price) < parseFloat(min.price) ? rate : min),
        rates[0]
      );

      setText(this.shippingValue, formatShippingPrice(cheapest));
      setText(this.shippingTotal, formatShippingPrice(cheapest));
      this.applyTotals(Math.round(parseFloat(cheapest.price) * 100));

      if (this.message instanceof HTMLElement) {
        if (rates.length > 1) {
          this.message.textContent = rates
            .map((rate) => `${maskRateLabel(rate, rates)}: ${formatShippingPrice(rate)}`)
            .join(' · ');
          this.message.hidden = false;
        } else {
          this.message.textContent = maskRateLabel(cheapest, rates);
          this.message.hidden = false;
        }
      }
    } catch (err) {
      if (requestId !== this.#requestId) return;

      setShimmer(this.shippingValue, false);
      setShimmer(this.shippingTotal, false);
      if (this.quoteBubble instanceof HTMLElement) this.quoteBubble.hidden = true;
      setText(this.shippingValue, '—');
      setText(this.shippingTotal, '—');
      this.applyTotals(null);
      if (this.message instanceof HTMLElement) {
        this.message.textContent = 'Não foi possível calcular o frete. Tente novamente.';
        this.message.hidden = false;
      }
    }
  };
}

if (!customElements.get('cart-shipping-cep')) {
  customElements.define('cart-shipping-cep', CartShippingCep);
}
